import { useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from './useAuth';

// NOTE on the mismatch between the `label` shown here and the object KEY:
// the keys ('collector', 'enthusiast', 'curator') are the values stored in
// user_profiles.plan and referenced by Stripe subscription metadata / the
// webhook's PLAN_FROM_PRICE map — renaming them would silently break plan
// detection for every existing subscriber, so they're left alone. Only the
// display `label` was wrong: the landing page has always called the €19/5-
// vehicle tier "Enthusiast" and the €49/15-vehicle tier "Collector", but this
// map had it backwards. Labels now match the landing page; keys are unchanged.
const PLAN_LIMITS = {
  free:       { vehicles: 2,         ai: true,  label: 'Free'       },   // AI on 2 vehicles — the differentiator is what sells the upgrade
  collector:  { vehicles: 5,         ai: true,  label: 'Enthusiast' },
  enthusiast: { vehicles: 15,        ai: true,  label: 'Collector'  },
  curator:    { vehicles: Infinity,  ai: true,  label: 'Curator'    },
};

export const usePlan = () => {
  const { user } = useAuth();
  const [plan, setPlan]           = useState('free');
  const [trialEndsAt, setTrialEndsAt] = useState(null);
  const [status, setStatus]       = useState('trialing');
  const [loading, setLoading]     = useState(true);

  useEffect(() => {
    if (!user) return;
    supabase
      .from('user_profiles')
      .select('plan, trial_ends_at, subscription_status')
      .eq('id', user.id)
      .single()
      .then(({ data }) => {
        if (data) {
          // Check if trial has expired
          const trialExpired = data.trial_ends_at && new Date(data.trial_ends_at) < new Date();
          const effectivePlan = data.subscription_status === 'active' ? data.plan : trialExpired ? 'free' : data.plan;
          setPlan(effectivePlan || 'free');
          setTrialEndsAt(data.trial_ends_at);
          setStatus(data.subscription_status || 'trialing');
        }
        setLoading(false);
      });
  }, [user]);

  const limits      = PLAN_LIMITS[plan] || PLAN_LIMITS.free;
  const isTrialing  = status === 'trialing' && trialEndsAt && new Date(trialEndsAt) > new Date();
  const trialDaysLeft = trialEndsAt ? Math.max(0, Math.ceil((new Date(trialEndsAt) - new Date()) / (1000 * 60 * 60 * 24))) : 0;

  // When a trial lapses without a subscription the account goes READ-ONLY: the
  // owner can still see their fleet, documents and valuations, but cannot add or
  // change anything until they choose a plan. Locking people out of their own
  // records converts some and permanently loses others.
  //
  // Mirrors public.fp_can_write() in SQL, which is the real enforcement — this is
  // only so the UI can explain itself rather than throwing database errors.
  const trialExpired = !!trialEndsAt && new Date(trialEndsAt) <= new Date();
  const isReadOnly =
    status === 'suspended' ||
    (trialExpired && !['active', 'past_due'].includes(status));

  return {
    plan,
    limits,
    loading,
    isTrialing,
    trialDaysLeft,
    trialExpired,
    isReadOnly,
    status,
    canAddVehicle: (currentCount) =>
      !isReadOnly && (isTrialing || currentCount < limits.vehicles),
    canUseAI: limits.ai,
    vehicleLimit: limits.vehicles,
    planLabel: limits.label,
  };
};